import { createHiveClient, unwrap, type Auth, type Me, type HiveClient, type Problem, type SignIn } from '@hive/api';
import { isLang, type Lang } from '@hive/i18n';
import { applyBrandColor, applyTheme, isTheme, type ThemePref } from '@hive/ui';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { apiBase } from './apiBase';
import { iconSrc } from './logo';

/**
 * Sessão da pessoa na plataforma: quem é (/me), o que pode (permissões do perfil ativo),
 * o cliente da API já com token e tenant, e as preferências (idioma, tema).
 */
export interface Session {
  tenant: SignIn;
  auth: Auth;
  api: HiveClient;
  me: Me;
  can: (perm: string) => boolean;
  refreshMe: () => Promise<Me>;
  setLang: (l: Lang) => Promise<void>;
  setTheme: (t: ThemePref) => Promise<void>;
  /** menu em trilho ou em árvore, guardado na conta */
  setNavStyle: (v: NavStyle) => Promise<void>;
  switchProfile: (profileId: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Problema de autenticação que derrubou a sessão (mostra "Entrar de novo"). */
  expired: Problem | null;
  clearExpired: () => void;
  /** R01-17 B: ação sensível pediu MFA recente (mostra "Confirmar identidade"). */
  stepUp: Problem | null;
  clearStepUp: () => void;
}

const Ctx = createContext<Session | null>(null);

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSession fora do SessionProvider');
  return s;
}

/** Só para telas que funcionam com e sem sessão. */
export function useOptionalSession(): Session | null {
  return useContext(Ctx);
}

export function createApi(o: {
  auth: Auth;
  slug: string;
  lang: () => Lang;
  onUnauthorized?: (p: Problem) => void;
  onStepUp?: (p: Problem) => void;
}): HiveClient {
  return createHiveClient({
    baseUrl: apiBase(),
    getToken: () => o.auth.getToken(),
    tenant: o.slug,
    getLang: o.lang,
    onUnauthorized: o.onUnauthorized,
    onStepUp: o.onStepUp,
  });
}

export const LANG_KEY = 'hive.lang';

export function rememberLang(l: Lang) {
  try {
    window.localStorage.setItem(LANG_KEY, l);
  } catch {
    /* sem armazenamento */
  }
}

export function rememberedLang(): Lang | null {
  try {
    const v = window.localStorage.getItem(LANG_KEY);
    return isLang(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * Marca na página: cor, nome na aba e, com o ícone quadrado enviado, o ícone da aba (sem ele, o do Hive). O logotipo
 * completo nunca vira ícone: espremido num quadrado de 16 px ficaria ilegível.
 */
export function applyBranding(b: Record<string, unknown> | undefined | null, slug?: string) {
  applyBrandColor(typeof b?.primaryColor === 'string' ? b.primaryColor : null);
  const name = typeof b?.productName === 'string' && b.productName.trim() ? b.productName.trim() : 'Hive';
  document.title = name;
  if (slug !== undefined) applyFavicon(iconSrc(slug, b?.iconUrl));
}

/** Troca o ícone da aba pelo da empresa (ou volta ao padrão do Hive guardado na primeira troca). */
export function applyFavicon(src: string | null) {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) return;
  link.dataset.hiveDefault ??= link.getAttribute('href') ?? '';
  link.dataset.hiveDefaultType ??= link.getAttribute('type') ?? '';
  if (src) {
    link.setAttribute('href', src);
    link.removeAttribute('type');
  } else {
    link.setAttribute('href', link.dataset.hiveDefault);
    if (link.dataset.hiveDefaultType) link.setAttribute('type', link.dataset.hiveDefaultType);
  }
}

export function SessionProvider({
  tenant,
  auth,
  initialMe,
  lang,
  onLang,
  children,
}: {
  tenant: SignIn;
  auth: Auth;
  initialMe: Me;
  lang: Lang;
  onLang: (l: Lang) => void;
  children: ReactNode;
}) {
  const [me, setMe] = useState<Me>(initialMe);
  const [expired, setExpired] = useState<Problem | null>(null);
  const [stepUp, setStepUp] = useState<Problem | null>(null);
  const langRef = useRef(lang);
  langRef.current = lang;
  const checking = useRef<Promise<void> | null>(null);
  const api = useMemo(
    () =>
      createApi({
        auth,
        slug: tenant.slug ?? '',
        lang: () => langRef.current,
        onStepUp: (p) => setStepUp(p),
        onUnauthorized: (p) => {
          // um 401 isolado (rota que recusou o token, token que venceu no caminho) não derruba a sessão: renova e só
          // encerra quando a renovação é recusada (sessão vencida, revogada ou reuso do refresh)
          if (!auth.renew) {
            setExpired((cur) => cur ?? p);
            return;
          }
          checking.current ??= auth
            .renew()
            .then((t) => {
              if (!t) setExpired((cur) => cur ?? p);
            })
            .catch(() => setExpired((cur) => cur ?? p))
            .finally(() => {
              checking.current = null;
            });
        },
      }),
    [auth, tenant.slug],
  );

  const refreshMe = useCallback(async () => {
    const m = await unwrap(api.GET('/api/v1/me'));
    setMe(m);
    applyBranding(m.tenant?.branding, tenant.slug);
    return m;
  }, [api, tenant.slug]);

  const value = useMemo<Session>(() => {
    const perms = new Set(me.permissions ?? []);
    return {
      tenant,
      auth,
      api,
      me,
      can: (p) => perms.has(p),
      refreshMe,
      async setLang(l) {
        onLang(l);
        rememberLang(l);
        const m = await unwrap(api.PUT('/api/v1/me/preferences', { body: { lang: l } }));
        setMe(m);
      },
      async setTheme(t) {
        applyTheme(t);
        const m = await unwrap(api.PUT('/api/v1/me/preferences', { body: { theme: t } }));
        setMe(m);
      },
      async setNavStyle(v) {
        applyNavStyle(v);
        const m = await unwrap(api.PUT('/api/v1/me/preferences', { body: { navStyle: v } }));
        setMe(m);
      },
      async switchProfile(profileId) {
        await unwrap(api.PUT('/api/v1/me/active-profile', { body: { profileId } }));
        // As permissões mudam na hora, sem novo login (HU-023).
        await refreshMe();
      },
      async signOut() {
        // HU-013: ao sair, o que ficou guardado no aparelho é apagado.
        // A sessão OIDC é apagada pelo próprio auth.signOut (precisa do id_token para encerrar no provedor).
        try {
          for (const k of Object.keys(window.sessionStorage)) if (k.startsWith('hive.') || k.startsWith('one.')) window.sessionStorage.removeItem(k);
          for (const k of Object.keys(window.localStorage)) if (k.startsWith('hive.wz.') || k.startsWith('one.wz.')) window.localStorage.removeItem(k);
        } catch {
          /* sem armazenamento */
        }
        await auth.signOut();
      },
      expired,
      clearExpired: () => setExpired(null),
      stepUp,
      clearStepUp: () => setStepUp(null),
    };
  }, [tenant, auth, api, me, refreshMe, onLang, expired, stepUp]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export type NavStyle = 'rail' | 'tree';

export function navStyleOf(me: Me | null | undefined): NavStyle {
  return me?.navStyle === 'tree' ? 'tree' : 'rail';
}

/** O estilo do menu vale pelo atributo na raiz (o CSS escolhe trilho ou árvore). */
export function applyNavStyle(v: NavStyle) {
  if (typeof document !== 'undefined') document.documentElement.dataset.nav = v;
}

export function themeOf(me: Me | null | undefined): ThemePref {
  return isTheme(me?.theme) ? me.theme : 'system';
}
